import React, { useEffect, useState } from 'react'
import { utils } from '@grammarly/badger-ui'
import { Document } from '@contentful/rich-text-types'

import { UrlBasedUsageTracker } from '@lib/utils/tool-usage'
import { TypeSingleColumn } from '@lib/types'
import {
  DEFAULT_FOOTER_BANNER_COPY,
  DEFAULT_NEXT_BEST_ACTION_COPY,
  DEFAULT_PLACEHOLDER_TEXT,
  DEFAULT_SUBMIT_BUTTON_TEXT,
  DEFAULT_TOOL_INPUT_NAME,
} from './constants'
import { ToolProps, ToolType } from './types'
import { useCheetahToolState } from './use-cheetah-tool-state'
import { getActualMaximumWordLimit, getActualMinimumWordLimit } from './utils'
import { SessionStorageKey } from '@lib/utils/session-storage'

interface ToolContextValue {
  toolType: ToolType
  toolInputName: string
  submitButtonText: string
  canUploadFile: boolean
  outputTitle: string
  maxWordLimit: number
  minWordLimit: number
  isLoading: boolean
  setIsLoading: (isLoading: boolean) => void
  outputContent: React.ReactNode | null
  setOutputContent: (outputContent: React.ReactNode) => void
  outputTextToCopy: string | null
  setOutputTextToCopy: (outputTextToCopy: string | null) => void
  nextBestActionCopy: string | null
  setNextBestActionCopy: (nextBestActionCopy: string | null) => void
  textLimitExceededCopy: string | undefined
  setTextLimitExceededCopy: (textLimitExceededCopy: string | undefined) => void
  toolError: string | null
  setToolError: (toolError: string | null) => void
  triggerUsageWall: () => void
  triggerTextLimitWall: () => void
  placeholder: string
  footerBannerCopy: string
  setShowToolsGoogleOneTap: (showGoogleOneTap: boolean) => void
  isEligibleForToolsGoogleOneTap: boolean
  toolUsageTracker?: UrlBasedUsageTracker
  llmPrompt?: Document
  signupWall?: TypeSingleColumn
  upgradeWall?: TypeSingleColumn
  sampleText?: string
}

const [BaseProvider, useTool] = utils.createContext<ToolContextValue>({
  name: 'Tool',
})
export type ToolProviderProps = ToolProps & {
  children: React.ReactNode
  triggerUsageWall: () => void
  triggerTextLimitWall: () => void
  toolUsageTracker?: UrlBasedUsageTracker
  isEligibleForToolsGoogleOneTap: boolean
  setShowToolsGoogleOneTap: (show: boolean) => void
}

export const ToolProvider = (props: ToolProviderProps) => {
  const {
    toolType,
    inputName,
    placeholder = DEFAULT_PLACEHOLDER_TEXT,
    submitButtonText = DEFAULT_SUBMIT_BUTTON_TEXT,
    canUploadFile = true,
    outputTitle,
    maxWordLimit,
    minWordLimit,
    footerBannerCopy = DEFAULT_FOOTER_BANNER_COPY,
    llmPrompt,
    sampleText,
    toolUsageTracker,
    triggerUsageWall,
    triggerTextLimitWall,
    signupWall,
    upgradeWall,
    children,
    isEligibleForToolsGoogleOneTap,
    setShowToolsGoogleOneTap,
  } = props

  const toolInputName = inputName ?? DEFAULT_TOOL_INPUT_NAME
  const [isLoading, setIsLoading] = useState(false)
  const [outputContent, setOutputContent] = useState<React.ReactNode | null>(null)
  const [outputTextToCopy, setOutputTextToCopy] = useState<string | null>(null)
  const [nextBestActionCopy, setNextBestActionCopy] = useState<string | null>(DEFAULT_NEXT_BEST_ACTION_COPY)
  const [textLimitExceededCopy, setTextLimitExceededCopy] = useState<string | undefined>(undefined)
  const [toolError, setToolError] = useState<string | null>(null)

  useEffect(() => {
    sessionStorage.setItem(SessionStorageKey.toolLastUsed, toolType)
  }, [toolType])

  useCheetahToolState(
    toolType,
    setIsLoading,
    setOutputContent,
    setOutputTextToCopy,
    setNextBestActionCopy,
    setToolError,
    setShowToolsGoogleOneTap,
    isEligibleForToolsGoogleOneTap
  )

  const actualMaximumWordLimit = getActualMaximumWordLimit(maxWordLimit)
  const actualMinimumWordLimit = getActualMinimumWordLimit(minWordLimit, actualMaximumWordLimit)

  return (
    <BaseProvider
      value={{
        toolType,
        toolInputName,
        isLoading,
        setIsLoading,
        outputContent,
        setOutputContent,
        outputTextToCopy,
        setOutputTextToCopy,
        nextBestActionCopy,
        setNextBestActionCopy,
        textLimitExceededCopy,
        setTextLimitExceededCopy,
        toolError,
        setToolError,
        toolUsageTracker,
        placeholder,
        submitButtonText,
        canUploadFile,
        outputTitle,
        maxWordLimit: actualMaximumWordLimit,
        minWordLimit: actualMinimumWordLimit,
        footerBannerCopy,
        llmPrompt,
        triggerUsageWall,
        triggerTextLimitWall,
        signupWall,
        upgradeWall,
        sampleText,
        isEligibleForToolsGoogleOneTap,
        setShowToolsGoogleOneTap,
      }}
    >
      {children}
    </BaseProvider>
  )
}

export { useTool }
